/**
 * @file useListingDetails.js
 * @brief Loads a listing detail record and exposes the actions used on the detail page.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useProtectedImages } from "./useProtectedImage";

/**
 * Fetch and manage the state needed by the listing detail view.
 * @param {Object} params Hook parameters.
 * @param {Object} params.api API client used for listing actions.
 * @param {string} params.listingId Listing identifier.
 * @param {Function} params.navigate React Router navigation callback.
 * @returns {Object} Listing data, protected images, loading state, and mutation helpers.
 */
export default function useListingDetails({ api, listingId, navigate }) {
    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const imageSourceUrls = useMemo(
        () => (listing?.images || []).map((img) => img.url),
        [listing]
    );

    const protectedImages = useProtectedImages(imageSourceUrls);
    const images = useMemo(() => protectedImages.filter(Boolean), [protectedImages]);

    const refreshListing = useCallback(async () => {
        const res = await api.get(`listings/${listingId}/`);
        setListing(res.data);
    }, [api, listingId]);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                await refreshListing();
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [refreshListing]);

    const toggleFavorite = useCallback(async () => {
        const res = await api.post(`listings/${listingId}/toggle-favorite/`);
        const { favorited, favorites_count } = res.data;
        setListing((prev) => ({ ...prev, is_favorited: favorited, favorites_count }));
    }, [api, listingId]);

    const updateStatus = useCallback(async (status) => {
        await api.patch(`listings/${listingId}/`, { status });
        await refreshListing();
    }, [api, listingId, refreshListing]);

    const deleteListing = useCallback(async () => {
        await api.delete(`listings/${listingId}/`);
        navigate(-1);
    }, [api, listingId, navigate]);

    return {
        listing,
        images,
        loading,
        error,
        refreshListing,
        toggleFavorite,
        updateStatus,
        deleteListing,
    };
}
